import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Input from "../components/common/Input";
import SelectField from "../components/common/SelectField";
import Button from "../components/common/Button";
import { SearchBar } from "../components/common/Table";
import useStore from "../store/useStore";
import api from "../services/api";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Users = ({ directory }) => {
  const { toast, user, authRole } = useStore();
  const role = authRole || user?.role;
  const isSuperAdmin = role === "SuperAdmin";
  const isCompanyDirectory = directory === "company" || role === "CompanyAdmin";
  const needsCompanyPicker = isSuperAdmin && directory === "company";
  const roleOptions = isCompanyDirectory ? ["CompanyAdmin", "Staff"] : ["SuperAdmin"];
  const defaultRole = isCompanyDirectory ? "Staff" : "SuperAdmin";

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState("");

  const emptyForm = useCallback(() => ({
    firstName: "",
    lastName: "",
    email: "",
    role: defaultRole,
    password: "",
    companyId: ""
  }), [defaultRole]);

  const [form, setForm] = useState(emptyForm);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = isSuperAdmin
        ? { scope: isCompanyDirectory ? "company" : "platform" }
        : undefined;
      const res = await api.get('/users', { params });
      setUsers(res.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast, isSuperAdmin, isCompanyDirectory]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!needsCompanyPicker) return undefined;

    const loadCompanies = async () => {
      try {
        const res = await api.get("/companies");
        setCompanies(res.data?.data || []);
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast("Failed to load companies", "error");
      }
    };

    loadCompanies();
  }, [needsCompanyPicker, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const clearForm = () => {
    setForm(emptyForm());
    setSelectedUser(null);
  };

  const handleAdd = async () => {
    if (!form.firstName || !form.firstName.trim()) {
      toast('First name is required', 'error');
      return;
    }

    if (!form.lastName || !form.lastName.trim()) {
      toast('Last name is required', 'error');
      return;
    }

    const email = form.email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      toast('A valid email is required', 'error');
      return;
    }

    if (!form.password || form.password.length < 8) {
      toast('Password must be at least 8 characters', 'error');
      return;
    }

    if (needsCompanyPicker && !form.companyId) {
      toast('Select a company', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email,
        password: form.password,
        role: form.role
      };

      if (needsCompanyPicker) {
        payload.companyId = Number(form.companyId);
      }

      await api.post('/users', payload);
      toast('User added successfully', 'success');
      clearForm();
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      toast(error.response?.data?.message || 'Failed to add user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) {
      toast('Please select a user to update', 'error');
      return;
    }

    if (!form.firstName || !form.firstName.trim()) {
      toast('First name is required', 'error');
      return;
    }

    if (!form.lastName || !form.lastName.trim()) {
      toast('Last name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        role: form.role
      };

      if (form.password && form.password.length < 8) {
        toast('Password must be at least 8 characters', 'error');
        setLoading(false);
        return;
      }

      if (form.password) {
        payload.password = form.password;
      }

      const email = form.email.trim().toLowerCase();
      if (!EMAIL_PATTERN.test(email)) {
        toast('A valid email is required', 'error');
        setLoading(false);
        return;
      }
      payload.email = email;

      await api.put(`/users/${selectedUser.id}`, payload);
      toast('User updated successfully', 'success');
      clearForm();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast(error.response?.data?.message || 'Failed to update user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) {
      toast('Please select a user to reset password', 'error');
      return;
    }

    if (!window.confirm(`Reset password for ${selectedUser.email}? A temporary password will be generated.`)) {
      return;
    }

    try {
      setLoading(true);
      const res = await api.post(`/users/${selectedUser.id}/reset-password`);
      const tempPassword = res.data?.data?.temporaryPassword;

      if (tempPassword) {
        try {
          await navigator.clipboard.writeText(tempPassword);
          toast(`Temporary password generated: ${tempPassword} (copied to clipboard)`, 'success');
        } catch {
          toast(`Temporary password generated: ${tempPassword}`, 'success');
        }
      } else {
        toast('Password reset successfully', 'success');
      }
      fetchUsers();
    } catch (error) {
      console.error('Error resetting password:', error);
      toast(error.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) {
      toast('Please select a user to delete', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/users/${selectedUser.id}`);
      toast('User deleted successfully', 'success');
      clearForm();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast('Failed to delete user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: roleOptions.includes(user.role) ? user.role : defaultRole,
      password: "",
      companyId: user.companyId ? String(user.companyId) : ""
    });
  };

  const filtered = users.filter(user =>
    (user.firstName || '').toLowerCase().includes(search.toLowerCase()) ||
    (user.lastName || '').toLowerCase().includes(search.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (user.role || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title={needsCompanyPicker ? "Company Users" : isSuperAdmin ? "System Administrators" : "Users"} />

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-6">
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
          <Input label="First Name" name="firstName" value={form.firstName} onChange={handleChange} required />
          <Input label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} required />
          <Input
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder="name@company.com"
          />
          {needsCompanyPicker && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Company <span className="text-red-500">*</span>
              </label>
              <select
                name="companyId"
                value={form.companyId}
                onChange={handleChange}
                className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 bg-transparent focus:border-teal-500"
              >
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.companyName}</option>
                ))}
              </select>
            </div>
          )}
          <SelectField label="Role" name="role" value={form.role} onChange={handleChange} options={roleOptions} />
          <Input
            label="Password"
            name="password"
            type="password"
            revealable
            autoComplete="new-password"
            value={form.password}
            onChange={handleChange}
            placeholder={selectedUser ? "Leave blank to keep current" : ""}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading || selectedUser !== null}>Add</Button>
          <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading || selectedUser === null}>Update</Button>
          <Button variant="secondary" icon="refresh" onClick={handleResetPassword} disabled={loading || selectedUser === null}>Reset Password</Button>
          <Button variant="danger" icon="delete" onClick={handleDelete} disabled={loading || selectedUser === null}>Delete</Button>
          <Button variant="secondary" icon="clear" onClick={clearForm} disabled={loading}>Clear</Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 max-w-md">
        <SearchBar
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email or role..."
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">First Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Last Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No users found. Click "Add" to create one.
                  </td>
                </tr>
              ) : (
                filtered.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${selectedUser?.id === user.id ? 'bg-teal-50' : ''}`}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900">{user.firstName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{user.lastName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{user.role}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Users;